import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useOptionalAuthGate } from "@/components/auth/auth-gate";
import { fetchCheckoutPlans, startCashfreeCheckout } from "@/lib/cashfree.functions";
import { openCashfreeCheckout } from "@/lib/cashfree-client";
import { popOverlay, pushOverlay } from "@/lib/overlay-store";

/**
 * Persistence gate (sign-in) plus the real upgrade surface: the modal lists
 * the live subscription plans from the database and opens Cashfree hosted
 * checkout with a session id minted on the server.
 */
type PremiumState = {
  allowed: boolean;
  checking: boolean;
  trialEnded: boolean;
  reason: string;
  requirePremium: () => boolean;
  openLock: () => void;
};

type UpgradeState = {
  openUpgrade: (reason?: string) => void;
  closeUpgrade: () => void;
};

const UpgradeContext = createContext<UpgradeState | null>(null);

/** Kept for error handling paths; nothing throws PREMIUM_REQUIRED anymore. */
export function isPremiumError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return message.includes("PREMIUM_REQUIRED");
}

export const PREMIUM_FEATURES = [
  "Unlimited tests, chapters & question tracking",
  "Full performance dashboard and AI insights",
  "Premium simulations, labs & toolkit modules",
  "What If? Lab — Premium Scenarios",
  "Sync Across All Devices",
  "Cloud Storage — Your Study Data & Progress",
  "Priority support",
];

export function formatInr(paise: number): string {
  const rupees = paise / 100;
  return `₹${Number.isInteger(rupees) ? rupees : rupees.toFixed(2)}`;
}

export type CheckoutPlanOption = {
  id: string;
  name: string;
  basePaise: number;
  addonPaise: number;
  simulationIncluded: boolean;
  duration: string | null;
};

/**
 * Simulation line for a plan at checkout: plans that include simulations show
 * a confirmation, every other plan shows the optional paid toggle.
 */
export function SimulationOption({
  plan,
  checked,
  disabled,
  onChange,
}: {
  plan: CheckoutPlanOption;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  if (plan.simulationIncluded) {
    return (
      <p className="mt-2 flex items-center gap-1.5 rounded-xl bg-primary/10 px-2.5 py-1.5 text-[11px] font-semibold text-primary">
        <Check className="size-3.5 shrink-0" aria-hidden />
        300 Simulations Included
      </p>
    );
  }
  return (
    <label className="mt-2 flex cursor-pointer items-center gap-2 rounded-xl border border-hairline bg-background/40 px-2.5 py-1.5 text-[11px] font-semibold">
      <input
        type="checkbox"
        className="size-4 accent-[hsl(var(--primary))]"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        Enable 300 Simulations
        <span className="text-primary"> +{formatInr(plan.addonPaise)}</span>
      </span>
    </label>
  );
}

/** Turns a Postgres interval such as "1 mon" into student-friendly text. */
export function formatDuration(duration: string | null): string {
  if (!duration) return "";
  const value = duration.trim().toLowerCase();
  const match = /^(\d+)\s*(year|yr|mon|month|day|week)/.exec(value);
  if (!match) return duration;
  const n = Number(match[1]);
  const unit = match[2].startsWith("mon")
    ? "month"
    : match[2].startsWith("y")
      ? "year"
      : match[2].startsWith("w")
        ? "week"
        : "day";
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

export function PremiumLockProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string | null>(null);

  const openUpgrade = useCallback((why?: string) => {
    setReason(why ?? null);
    setOpen(true);
    pushOverlay();
  }, []);

  const closeUpgrade = useCallback(() => {
    setOpen(false);
    setReason(null);
    popOverlay();
  }, []);

  const value = useMemo<UpgradeState>(
    () => ({ openUpgrade, closeUpgrade }),
    [openUpgrade, closeUpgrade],
  );

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <UpgradeModal open={open} reason={reason} onClose={closeUpgrade} />
    </UpgradeContext.Provider>
  );
}

export function useUpgrade(): UpgradeState {
  const ctx = useContext(UpgradeContext);
  return ctx ?? { openUpgrade: () => {}, closeUpgrade: () => {} };
}

export function usePremium(): PremiumState {
  const gate = useOptionalAuthGate();
  const allowed = gate ? gate.isAuthenticated : true;
  return {
    allowed,
    checking: false,
    trialEnded: false,
    reason: allowed ? "OK" : "AUTH_REQUIRED",
    requirePremium: () => (gate ? gate.requireAuth() : true),
    openLock: () => gate?.openAuthModal(),
  };
}

export function PremiumGate({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/** Drop-in CTA for quota walls. */
export function UpgradeButton({
  label = "Upgrade to Premium",
  reason,
  className = "",
}: {
  label?: string;
  reason?: string;
  className?: string;
}) {
  const { openUpgrade } = useUpgrade();
  return (
    <button
      type="button"
      onClick={() => openUpgrade(reason)}
      className={`inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-sm transition active:scale-[0.98] ${className}`}
    >
      <Sparkles className="size-3.5" aria-hidden />
      {label}
    </button>
  );
}

function UpgradeModal({
  open,
  reason,
  onClose,
}: {
  open: boolean;
  reason: string | null;
  onClose: () => void;
}) {
  const { user, loading } = useAuth();
  const gate = useOptionalAuthGate();
  const startCheckout = useServerFn(startCashfreeCheckout);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [simPlans, setSimPlans] = useState<Record<string, boolean>>({});

  const plansQuery = useQuery({
    queryKey: ["checkout-plans"],
    queryFn: () => fetchCheckoutPlans(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const signedIn = Boolean(user) && !loading;

  const buy = async (planId: string, addonSelected: boolean) => {
    setError(null);
    if (!signedIn) {
      onClose();
      gate?.openAuthModal();
      return;
    }
    setBusyPlan(planId);
    try {
      const session = await startCheckout({
        data: {
          plan_id: planId,
          simulation_addon_selected: addonSelected,
          payment_type: "new_subscription",
        },
      });
      await openCashfreeCheckout({
        paymentSessionId: session.paymentSessionId,
        mode: session.mode,
      });
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err ?? "");
      setError(
        raw.includes("CASHFREE_NOT_CONFIGURED")
          ? "Payments are not switched on yet. Please try again shortly."
          : raw.includes("NOT_AUTHENTICATED")
            ? "Please sign in again to continue."
            : "We couldn't open the payment page. Please try again.",
      );
      setBusyPlan(null);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Upgrade to Premium"
            className="glass max-h-[88dvh] w-full max-w-md overflow-y-auto rounded-t-3xl border border-hairline p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:rounded-3xl sm:pb-5"
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                  <Sparkles className="size-3.5" aria-hidden /> Premium
                </p>
                <h2 className="mt-1 text-lg font-extrabold tracking-tight">Unlock unlimited</h2>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {reason ?? "Go beyond the free plan limits and keep every tracker unlimited."}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded-full border border-hairline p-1.5 text-muted-foreground"
              >
                <X className="size-4" />
              </button>
            </div>

            <ul className="mt-4 space-y-1.5">
              {PREMIUM_FEATURES.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Check className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 space-y-2">
              {plansQuery.isLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" aria-hidden /> Loading plans…
                </div>
              ) : plansQuery.data && plansQuery.data.plans.length > 0 ? (
                plansQuery.data.plans.map((plan) => {
                  const addonSelected = simPlans[plan.id] === true;
                  const total = plan.basePaise + (addonSelected ? plan.addonPaise : 0);
                  return (
                    <div
                      key={plan.id}
                      className="rounded-2xl border border-hairline bg-card/60 px-4 py-3"
                    >
                      <button
                        type="button"
                        disabled={busyPlan !== null}
                        onClick={() => void buy(plan.id, addonSelected)}
                        className="flex w-full items-center justify-between gap-3 text-left transition active:scale-[0.99] disabled:opacity-60"
                      >
                        <span>
                          <span className="block text-sm font-bold tracking-tight">
                            {plan.name}
                          </span>
                          <span className="block text-[11px] text-muted-foreground">
                            {formatDuration(plan.duration) || "Subscription"}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="text-base font-extrabold">{formatInr(total)}</span>
                          {busyPlan === plan.id ? (
                            <Loader2 className="size-4 animate-spin text-primary" aria-hidden />
                          ) : null}
                        </span>
                      </button>
                      <SimulationOption
                        plan={plan}
                        checked={addonSelected}
                        disabled={busyPlan !== null}
                        onChange={(next: boolean) =>
                          setSimPlans((prev) => ({ ...prev, [plan.id]: next }))
                        }
                      />
                    </div>
                  );
                })
              ) : (
                <p className="py-4 text-center text-xs text-muted-foreground">
                  Plans are unavailable right now. Please try again in a moment.
                </p>
              )}
            </div>

            {error ? (
              <p role="alert" className="mt-3 text-xs font-medium text-destructive">
                {error}
              </p>
            ) : null}

            <p className="mt-4 text-center text-[10px] leading-relaxed text-muted-foreground">
              Secure payment by Cashfree. UPI, cards, net banking and wallets supported.
            </p>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
